import { useState } from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { useCreateUser } from '@/hooks/useUsers';

interface FormData {
  name: string;
  email: string;
  password: string;
  role: 'admin' | 'user';
}

interface FormErrors {
  name?: string;
  email?: string;
  password?: string;
  role?: string;
}

interface AddUserModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * AddUserModal Component
 *
 * Modal dialog for creating a new user (admin only).
 * Matches the approved UI design in docs/views/settings.html lines 1069-1142.
 *
 * Tasks: M4.5.3.1, M4.5.3.2, M4.5.3.3, M4.5.3.4, M4.5.3.5
 */
export function AddUserModal({ open, onClose }: AddUserModalProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    password: '',
    role: 'user',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const createUser = useCreateUser();

  // Reset form when modal closes - using onClose callback pattern
  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'user',
    });
    setErrors({});
    setShowPassword(false);
    onClose();
  };

  // Handle input changes
  const handleChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field error on change
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Validate form fields
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Name validation (M4.5.3.2)
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Name must be 100 characters or less';
    }

    // Email validation (M4.5.3.3)
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation (M4.5.3.4)
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await createUser.mutateAsync({
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        role: formData.role,
      });
      onClose();
    } catch {
      // Error is handled by the mutation's onError callback
    }
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogContent className="w-full max-w-md p-6" onClose={handleClose}>
        <DialogHeader className="p-0 mb-4">
          <DialogTitle className="text-lg font-semibold text-foreground">Add New User</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Full Name Input (M4.5.3.2) */}
          <Input
            value={formData.name}
            onChange={e => handleChange('name', e.target.value)}
            label="Full Name"
            placeholder="John Smith"
            required
            error={errors.name}
            disabled={createUser.isPending}
          />

          {/* Email Input (M4.5.3.3) */}
          <Input
            type="email"
            value={formData.email}
            onChange={e => handleChange('email', e.target.value)}
            label="Email Address"
            placeholder="john@company.com"
            required
            error={errors.email}
            disabled={createUser.isPending}
          />

          {/* Password Input (M4.5.3.4) */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-foreground">
              Password <span className="text-destructive">*</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={e => handleChange('password', e.target.value)}
                className={`flex h-10 w-full rounded-md border bg-background px-3 py-2 pr-10 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                  errors.password ? 'border-destructive' : 'border-input'
                }`}
                placeholder="Minimum 8 characters"
                disabled={createUser.isPending}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.password ? (
              <p className="text-sm text-destructive">{errors.password}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Password must be at least 8 characters
              </p>
            )}
          </div>

          {/* Role Selector (M4.5.3.5) */}
          <Select
            value={formData.role}
            onChange={e => handleChange('role', e.target.value as 'admin' | 'user')}
            label="Role"
            error={errors.role}
            disabled={createUser.isPending}
          >
            <option value="user">Standard User</option>
            <option value="admin">Administrator</option>
          </Select>
          <p className="text-xs text-muted-foreground -mt-2">
            Administrators can manage users and delete records
          </p>

          {/* Footer Actions */}
          <DialogFooter className="p-0 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              disabled={createUser.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createUser.isPending}>
              {createUser.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create User
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
